import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_Animations() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎬 Advanced CSS: Animations</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS Animations allow you to animate elements using{" "}
        <code>@keyframes</code>. Unlike transitions, you can define multiple
        steps, loops, and timing functions to create complex animations without
        JavaScript.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>@keyframes</code> to define animation steps.
          </li>
          <li>
            <code>animation-name</code> specifies the keyframes to use.
          </li>
          <li>
            <code>animation-duration</code> defines how long one cycle lasts.
          </li>
          <li>
            <code>animation-timing-function</code> controls acceleration.
          </li>
          <li>
            Use <code>animation-delay</code>, <code>iteration-count</code>, and{" "}
            <code>animation-direction</code> for advanced control.
          </li>
        </ul>
      </div>

      {/* Fade In */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Fade In Animation</h2>
        <CodeCopyBlock
          code={`@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

div {
  animation: fadeIn 2s ease-in-out;
}`}
        />
        <div
          className="fadeIn"
          style={{
            width: "150px",
            height: "150px",
            backgroundColor: "#3b82f6",
            margin: "1rem",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Bounce */}
      <div className="tutorial-section">
        <h2 className="section-title">🏀 Bounce Animation</h2>
        <CodeCopyBlock
          code={`@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-50px); }
}

div {
  animation: bounce 1s infinite;
}`}
        />
        <div
          className="bounce"
          style={{
            width: "100px",
            height: "100px",
            backgroundColor: "#f59e0b",
            margin: "1rem",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Rotate */}
      <div className="tutorial-section">
        <h2 className="section-title">🔄 Rotate Animation</h2>
        <CodeCopyBlock
          code={`@keyframes rotate360 {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

div {
  animation: rotate360 3s linear infinite;
}`}
        />
        <div
          className="rotate360"
          style={{
            width: "120px",
            height: "120px",
            backgroundColor: "#10b981",
            margin: "1rem",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Color Change */}
      <div className="tutorial-section">
        <h2 className="section-title">🌈 Color Animation</h2>
        <CodeCopyBlock
          code={`@keyframes colorChange {
  0% { background-color: #3b82f6; }
  50% { background-color: #f59e0b; }
  100% { background-color: #3b82f6; }
}

div {
  animation: colorChange 4s infinite;
}`}
        />
        <div
          className="colorChange"
          style={{
            width: "150px",
            height: "150px",
            margin: "1rem",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Move + Rotate */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Move & Rotate</h2>
        <CodeCopyBlock
          code={`@keyframes transformAnim {
  0% { transform: translateX(0) rotate(0deg); }
  50% { transform: translateX(100px) rotate(180deg); }
  100% { transform: translateX(0) rotate(360deg); }
}

div {
  animation: transformAnim 3s infinite ease-in-out;
}`}
        />
        <div
          className="transformAnim"
          style={{
            width: "120px",
            height: "120px",
            backgroundColor: "#8b5cf6",
            margin: "1rem",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Combine multiple animations on one element.</li>
          <li>
            Use <code>animation-delay</code> and{" "}
            <code>animation-iteration-count</code> for advanced timing.
          </li>
          <li>Create interactive loaders, spinners, and UI effects.</li>
          <li>
            Combine animations with 2D/3D transforms for dynamic elements.
          </li>
          <li>
            Experiment with CSS variables to control animation parameters
            dynamically.
          </li>
        </ul>
      </div>
    </div>
  );
}
